import { createBrowserRouter, Navigate, Outlet, useParams, type RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import {
  AppShell,
  ProtectedRoute,
  ProfileGate,
  AdminRoute,
  DataCollectionRoute,
  QARoute,
  SuperAdminRoute,
  ParticipantRoute,
  SchedulingRoute,
} from '@/components/layout';
import { LoginPage } from '@/pages/LoginPage';
import { SignUpPage } from '@/pages/SignUpPage';
import { EmailVerificationPage } from '@/pages/EmailVerificationPage';
import { AuthActionPage } from '@/pages/AuthActionPage';
import { SessionTimer } from '@/components/recording';
import { useAuth } from '@/lib/auth-context';
import { canAccessAgents, canAccessAppBench, canAccessPhysicalAi } from '@/lib/product-flags';
import { appName } from '@/lib/app-surface';

import { RootRedirect } from '@/components/layout/RootRedirect';

const AdminDashboardPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/DashboardPage'));
const IDVPage = /* @__PURE__ */ lazy(() => import('@/pages/IDVPage'));
const ConsentPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/ConsentPage').then((module) => ({ default: module.ConsentPage })),
);
const ProfilePage = /* @__PURE__ */ lazy(() =>
  import('@/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })),
);
const BillingPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/BillingPage').then((module) => ({ default: module.BillingPage })),
);
const RecordingPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/RecordingPage').then((module) => ({ default: module.RecordingPage })),
);
const HomePage = /* @__PURE__ */ lazy(() =>
  import('@/pages/HomePage').then((module) => ({ default: module.HomePage })),
);
const ImageUploadPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/ImageUploadPage').then((module) => ({ default: module.ImageUploadPage })),
);
const AnnotationPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/AnnotationPage').then((module) => ({ default: module.AnnotationPage })),
);
const ProjectsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/account/ProjectsPage').then((module) => ({ default: module.ProjectsPage })),
);
const MyOrganizationPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/MyOrganizationPage'));
const QADashboardPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/qa/QADashboardPage').then((module) => ({ default: module.QADashboardPage })),
);
const TenantsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/super-admin/TenantsPage').then((module) => ({ default: module.TenantsPage })),
);
const SuperAdminAppBenchDatasetsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/super-admin/AppBenchDatasetsPage').then((module) => ({
    default: module.AppBenchDatasetsPage,
  })),
);
const AppBenchDatasetDetailPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/appbench/AppBenchDatasetDetailPage'),
);
const CreateProjectPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/CreateProjectPage').then((module) => ({
    default: module.CreateProjectPage,
  })),
);
const ProjectWorkspaceLayout = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectWorkspaceLayout').then((module) => ({
    default: module.ProjectWorkspaceLayout,
  })),
);
const ProjectOverviewPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectOverviewPage').then((module) => ({
    default: module.ProjectOverviewPage,
  })),
);
const ProjectQueuePage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectQueuePage').then((module) => ({
    default: module.ProjectQueuePage,
  })),
);
const ProjectFilesPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectFilesPage').then((module) => ({
    default: module.ProjectFilesPage,
  })),
);
const ProjectWorkflowPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectWorkflowPage').then((module) => ({
    default: module.ProjectWorkflowPage,
  })),
);
const ProjectAnalyticsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectAnalyticsPage').then((module) => ({
    default: module.ProjectAnalyticsPage,
  })),
);
const ProjectUsersPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectUsersPage').then((module) => ({
    default: module.ProjectUsersPage,
  })),
);
const ProjectAgentsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectAgentsPage').then((module) => ({
    default: module.ProjectAgentsPage,
  })),
);
const ProjectSettingsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectSettingsPage').then((module) => ({
    default: module.ProjectSettingsPage,
  })),
);
const ProjectTranscriptsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/admin/project-workspace/ProjectTranscriptsPage').then((module) => ({
    default: module.ProjectTranscriptsPage,
  })),
);
const ParticipantProjectLayout = /* @__PURE__ */ lazy(() =>
  import('@/pages/participant-project/ParticipantProjectLayout').then((module) => ({
    default: module.ParticipantProjectLayout,
  })),
);
const ParticipantProjectOverviewPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/participant-project/ParticipantProjectOverviewPage').then((module) => ({
    default: module.ParticipantProjectOverviewPage,
  })),
);
const ParticipantQualityAuditPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/participant-project/ParticipantQualityAuditPage').then((module) => ({
    default: module.ParticipantQualityAuditPage,
  })),
);
const ParticipantAnnotationPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/participant-project/ParticipantAnnotationPage').then((module) => ({
    default: module.ParticipantAnnotationPage,
  })),
);
const RedTeamProvidersPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/red-team/RedTeamProvidersPage'));
const RedTeamCampaignsPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/red-team/RedTeamCampaignsPage'));
const RedTeamCampaignDashboardPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/red-team/RedTeamCampaignDashboardPage'),
);
const RedTeamWorkspacePage = /* @__PURE__ */ lazy(() => import('@/pages/red-team/RedTeamWorkspacePage'));
const RedTeamHomePage = /* @__PURE__ */ lazy(() => import('@/pages/red-team/RedTeamHomePage'));
const BenchmarkHomePage = /* @__PURE__ */ lazy(() => import('@/pages/admin/BenchmarkHomePage'));
const AppBenchLayout = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchLayout'));
const AppBenchConnectionsPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/appbench/AppBenchConnectionsPage'),
);
const AppBenchBenchmarksPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchBenchmarksPage'));
const AppBenchLiveMonitorPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/appbench/AppBenchLiveMonitorPage'),
);
const AppBenchTaskLiveMonitorPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/appbench/AppBenchTaskLiveMonitorPage'),
);
const AppBenchReportsPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchReportsPage'));
const AppBenchDashboardPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchDashboardPage'));
const AppBenchOperationsPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchOperationsPage'));
const AppBenchAppsPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchAppsPage'));
const AppBenchRunsPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/appbench/AppBenchRunsPage'));
const PromptBenchPage = /* @__PURE__ */ lazy(() => import('@/pages/promptbench/PromptBenchPage'));
const PhysicalAiPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/physical-ai/PhysicalAiPage'));
const PhysicalAiWork = /* @__PURE__ */ lazy(() => import('@/pages/admin/physical-ai/PhysicalAiWork'));
const PhysicalAiWizardPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/physical-ai/PhysicalAiWizardPage'));
const PhysicalAiProjectPage = /* @__PURE__ */ lazy(() => import('@/pages/admin/physical-ai/PhysicalAiProjectPage'));
const SchedulingStudiesPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudiesPage'));
const SchedulingStudySetupPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudySetupPage'));
const SchedulingStudyLayout = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyLayout'));
const SchedulingStudyIndexRedirect = /* @__PURE__ */ lazy(() =>
  import('@/pages/scheduling/StudyLayout').then((module) => ({ default: module.StudyIndexRedirect })),
);
const SchedulingOverviewPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyOverviewPage'));
const SchedulingTeamPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyTeamPage'));
const SchedulingSettingsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudySettingsPage'));
const SchedulingParticipantsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyParticipantsPage'));
const SchedulingUploadsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyUploadsPage'));
const SchedulingApprovalsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyApprovalsPage'));
const SchedulingCalendarPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyCalendarPage'));
const SchedulingIncentivesPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyIncentivesPage'));
const SchedulingFollowUpsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyFollowUpsPage'));
const SchedulingNotificationsPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/StudyNotificationsPage'));
const SchedulingRsvpPage = /* @__PURE__ */ lazy(() => import('@/pages/scheduling/RsvpPage'));
const PhysicalAiCompatibilityPage = /* @__PURE__ */ lazy(
  () => import('@/pages/admin/physical-ai/PhysicalAiCompatibilityPage'),
);
const OrcaProjectsPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/ProjectsPage'));
const OrcaProjectPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/ProjectPage'));
const OrcaLaunchProjectPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/LaunchProjectPage'));
const OrcaAgentsPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AgentsPage'));
const OrcaAgentEditorPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AgentEditorPage'));
const OrcaEnvironmentsPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/EnvironmentsPage'));
const OrcaKeysPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/KeysPage'));
const OrcaAppsPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AppsPage'));
const OrcaDevelopersPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/DevelopersPage'));
const OrcaAppViewerPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/viewers/AppViewerPage'));
const OrcaApprovalsPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/ApprovalsPage'));
const OrcaPlaybookPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/PlaybookPage'));
const OrcaAutomationsListPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AutomationsPage').then((module) => ({ default: module.AutomationsListPage })));
const OrcaAutomationEditorPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AutomationsPage').then((module) => ({ default: module.AutomationEditorPage })));
const OrcaAutomationDetailPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/AutomationsPage').then((module) => ({ default: module.AutomationDetailPage })));
const OrcaBillingPage = /* @__PURE__ */ lazy(() => import('@/pages/orca/BillingPage'));
const OrcaCreditsPage = /* @__PURE__ */ lazy(() =>
  import('@/pages/super-admin/OrcaCreditsPage').then((module) => ({ default: module.OrcaCreditsPage })),
);
const PrivacyPolicyPage = /* @__PURE__ */ lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfUsePage = /* @__PURE__ */ lazy(() => import('@/pages/TermsOfUsePage'));

function RedTeamRoute() {
  const { user } = useAuth();
  return user?.products?.red_team ? <Outlet /> : <Navigate to="/" replace />;
}

function AgentsRoute() {
  const { user } = useAuth();
  return canAccessAgents(user) ? <Outlet /> : <Navigate to="/" replace />;
}

function AppBenchRoute() {
  const { user } = useAuth();
  return canAccessAppBench(user) ? <Outlet /> : <Navigate to="/" replace />;
}

function PhysicalAiRoute() {
  const { user } = useAuth();
  return canAccessPhysicalAi(user) ? <Outlet /> : <Navigate to="/" replace />;
}

function deferred(element: React.ReactNode) {
  return (
    <Suspense
      fallback={
        <div
          className="m-5 h-56 animate-pulse rounded-xl border border-atlas-border bg-white"
          aria-label="Loading page"
        />
      }
    >
      {element}
    </Suspense>
  );
}

function NotFoundPage() {
  return (
    <div className="grid min-h-full place-items-center bg-atlas-canvas p-6">
      <div className="atlas-panel max-w-lg p-10 text-center">
        <div className="atlas-kicker">Error 404</div>
        <h1 className="atlas-heading mt-2">This page does not exist</h1>
        <p className="mt-3 text-sm leading-6 text-atlas-muted">
          The address may have changed, or you may not have access to this workspace.
        </p>
        <a href="/" className="atlas-button-primary mt-6">
          Return to {appName}
        </a>
      </div>
    </div>
  );
}

function LegacyProjectRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/projects/${id}/overview`} replace />;
}

/** Recruiters and schedulers only work in Study Scheduling; other account areas send them back to their studies. */
function NotForSchedulingStaff() {
  const { user } = useAuth();
  if (user?.role === 'recruiter' || user?.role === 'scheduler') return <Navigate to="/scheduling" replace />;
  return <Outlet />;
}

// Build-time app selection. Plain comparisons on import.meta.env fold to constants,
// so each app's bundle leaves out the other app's pages entirely.
const INCLUDES_COLLECT = import.meta.env.VITE_APP_SURFACE !== 'orca';
const INCLUDES_ORCA = import.meta.env.VITE_APP_SURFACE !== 'collect';

/** Data Collection (dexter.hammerheadtech.co): collection, annotation, QA and Study Scheduling. */
const collectAccountRoutes: RouteObject[] = INCLUDES_COLLECT
  ? [
      { path: 'billing', element: deferred(<BillingPage />) },
    ]
  : [];

const collectRoutes: RouteObject[] = INCLUDES_COLLECT
  ? [
      {
        element: <SchedulingRoute />,
        children: [
          { path: 'scheduling', element: deferred(<SchedulingStudiesPage />) },
          { path: 'scheduling/new', element: deferred(<SchedulingStudySetupPage />) },
          { path: 'scheduling/studies/:studyId/setup', element: deferred(<SchedulingStudySetupPage />) },
          {
            path: 'scheduling/studies/:studyId',
            element: deferred(<SchedulingStudyLayout />),
            children: [
              { index: true, element: deferred(<SchedulingStudyIndexRedirect />) },
              { path: 'overview', element: deferred(<SchedulingOverviewPage />) },
              { path: 'calendar', element: deferred(<SchedulingCalendarPage />) },
              { path: 'participants', element: deferred(<SchedulingParticipantsPage />) },
              { path: 'uploads', element: deferred(<SchedulingUploadsPage />) },
              { path: 'approvals', element: deferred(<SchedulingApprovalsPage />) },
              { path: 'follow-ups', element: deferred(<SchedulingFollowUpsPage />) },
              { path: 'notifications', element: deferred(<SchedulingNotificationsPage />) },
              { path: 'incentives', element: deferred(<SchedulingIncentivesPage />) },
              { path: 'team', element: deferred(<SchedulingTeamPage />) },
              { path: 'settings', element: deferred(<SchedulingSettingsPage />) },
            ],
          },
        ],
      },
      // Participant-only routes
      {
        element: <ParticipantRoute />,
        children: [
          { path: 'identity-verification', element: deferred(<IDVPage />) },
          { path: 'consent', element: deferred(<ConsentPage />) },

          // Routes gated by profile completion (Requirement 3.1)
          {
            element: <ProfileGate />,
            children: [
              { path: 'dashboard', element: deferred(<HomePage />) },
              {
                path: 'record',
                element: <SessionTimer>{deferred(<RecordingPage />)}</SessionTimer>,
              },
              {
                path: 'upload-images',
                element: (
                  <SessionTimer mode="collection">{deferred(<ImageUploadPage />)}</SessionTimer>
                ),
              },
              { path: 'annotate', element: deferred(<AnnotationPage />) },
              {
                path: 'projects/:projectId',
                element: deferred(<ParticipantProjectLayout />),
                children: [
                  { index: true, element: <Navigate to="overview" replace /> },
                  { path: 'overview', element: deferred(<ParticipantProjectOverviewPage />) },
                  {
                    path: 'recording',
                    element: <SessionTimer>{deferred(<RecordingPage />)}</SessionTimer>,
                  },
                  {
                    path: 'collection',
                    element: (
                      <SessionTimer mode="collection">
                        {deferred(<ImageUploadPage />)}
                      </SessionTimer>
                    ),
                  },
                  { path: 'annotation', element: deferred(<ParticipantAnnotationPage />) },
                  { path: 'qa', element: deferred(<ParticipantQualityAuditPage />) },
                ],
              },
              { path: 'session', element: <div>Session</div> },
            ],
          },
        ],
      },
      // QA routes
      {
        path: 'qa',
        element: <QARoute />,
        children: [{ path: 'dashboard', element: deferred(<QADashboardPage />) }],
      },
    ]
  : [];

const collectAdminRoutes: RouteObject[] = INCLUDES_COLLECT
  ? [
      {
        element: <DataCollectionRoute />,
        children: [
          { path: 'dashboard', element: deferred(<AdminDashboardPage />) },
          { path: 'projects/new', element: deferred(<CreateProjectPage />) },
          { path: 'projects/:id/edit', element: <Navigate to="../settings" replace /> },
          { path: 'project/:id', element: <LegacyProjectRedirect /> },
          {
            path: 'projects/:id',
            element: deferred(<ProjectWorkspaceLayout />),
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: deferred(<ProjectOverviewPage />) },
              { path: 'queue', element: deferred(<ProjectQueuePage />) },
              { path: 'files', element: deferred(<ProjectFilesPage />) },
              { path: 'transcripts', element: deferred(<ProjectTranscriptsPage />) },
              { path: 'workflow', element: deferred(<ProjectWorkflowPage />) },
              { path: 'analytics', element: deferred(<ProjectAnalyticsPage />) },
              { path: 'users', element: deferred(<ProjectUsersPage />) },
              { path: 'agents', element: deferred(<ProjectAgentsPage />) },
              { path: 'settings', element: deferred(<ProjectSettingsPage />) },
            ],
          },
          { path: 'account/projects', element: deferred(<ProjectsPage />) },
          { path: 'account/*', element: <Navigate to="/admin/account/projects" replace /> },
        ],
      },
    ]
  : [];

/** Orca (orca.hammerheadtech.co): agents, Red Team, PromptBench, AppBench and Physical AI. */
const orcaAccountRoutes: RouteObject[] = INCLUDES_ORCA
  ? [
      { path: 'promptbench/*', element: deferred(<PromptBenchPage />) },
      { path: 'physical-ai/work', element: deferred(<PhysicalAiWork />) },
    ]
  : [];

const orcaAdminRoutes: RouteObject[] = INCLUDES_ORCA
  ? [
      {
        element: <PhysicalAiRoute />,
        children: [
          { path: 'physical-ai', element: deferred(<PhysicalAiPage />) },
          { path: 'physical-ai/new', element: deferred(<PhysicalAiWizardPage />) },
          {
            path: 'physical-ai/projects/:projectId/setup/:step',
            element: deferred(<PhysicalAiWizardPage />),
          },
          {
            path: 'physical-ai/projects/:projectId/:tab?',
            element: deferred(<PhysicalAiProjectPage />),
          },
          {
            path: 'physical-ai/compatibility',
            element: deferred(<PhysicalAiCompatibilityPage />),
          },
        ],
      },
      { path: 'red-team/providers', element: deferred(<RedTeamProvidersPage />) },
      { path: 'red-team/campaigns', element: deferred(<RedTeamCampaignsPage />) },
      {
        path: 'red-team/campaigns/:campaignId',
        element: deferred(<RedTeamCampaignDashboardPage />),
      },
      {
        element: <AppBenchRoute />,
        children: [
          {
            path: 'appbench',
            element: deferred(<AppBenchLayout />),
            children: [
              { index: true, element: <Navigate to="dashboard" replace /> },
              { path: 'dashboard', element: deferred(<AppBenchDashboardPage />) },
              { path: 'datasets', element: deferred(<BenchmarkHomePage />) },
              {
                path: 'datasets/:datasetId',
                element: deferred(<AppBenchDatasetDetailPage />),
              },
              { path: 'apps', element: deferred(<AppBenchAppsPage />) },
              { path: 'connections', element: deferred(<AppBenchConnectionsPage />) },
              { path: 'benchmarks', element: deferred(<AppBenchBenchmarksPage />) },
              { path: 'runs', element: deferred(<AppBenchRunsPage />) },
              { path: 'live-monitor', element: deferred(<AppBenchLiveMonitorPage />) },
              {
                path: 'live-monitor/runs/:runId/tasks/:taskId',
                element: deferred(<AppBenchTaskLiveMonitorPage />),
              },
              { path: 'reports', element: deferred(<AppBenchReportsPage />) },
              {
                path: 'usage-budgets',
                element: deferred(<AppBenchOperationsPage view="usage" />),
              },
              {
                path: 'settings-audit',
                element: deferred(<AppBenchOperationsPage view="settings" />),
              },
            ],
          },
        ],
      },
    ]
  : [];

const orcaRoutes: RouteObject[] = INCLUDES_ORCA
  ? [
      {
        element: <AgentsRoute />,
        children: [
          { path: 'agents', element: <Navigate to="/agents/projects" replace /> },
          { path: 'agents/projects', element: deferred(<OrcaProjectsPage />) },
          { path: 'agents/projects/new', element: deferred(<OrcaLaunchProjectPage />) },
          { path: 'agents/projects/:projectId', element: deferred(<OrcaProjectPage />) },
          { path: 'agents/agents', element: deferred(<OrcaAgentsPage />) },
          { path: 'agents/templates', element: <Navigate to="/agents/agents?view=templates" replace /> },
          { path: 'agents/agents/:agentId', element: deferred(<OrcaAgentEditorPage />) },
          { path: 'agents/agents/:agentId/playbook', element: deferred(<OrcaPlaybookPage />) },
          { path: 'agents/environments', element: deferred(<OrcaEnvironmentsPage />) },
          { path: 'agents/keys', element: deferred(<OrcaKeysPage />) },
          { path: 'agents/apps', element: deferred(<OrcaAppsPage />) },
          { path: 'agents/apps/:provider', element: deferred(<OrcaAppViewerPage />) },
          { path: 'agents/developers', element: deferred(<OrcaDevelopersPage />) },
          { path: 'agents/approvals', element: deferred(<OrcaApprovalsPage />) },
          { path: 'agents/automations', element: deferred(<OrcaAutomationsListPage />) },
          { path: 'agents/automations/new', element: deferred(<OrcaAutomationEditorPage />) },
          { path: 'agents/automations/:automationId', element: deferred(<OrcaAutomationDetailPage />) },
          { path: 'agents/automations/:automationId/edit', element: deferred(<OrcaAutomationEditorPage />) },
          { path: 'agents/billing', element: deferred(<OrcaBillingPage />) },
        ],
      },
      {
        element: <RedTeamRoute />,
        children: [
          { path: 'red-team', element: deferred(<RedTeamHomePage />) },
          {
            path: 'red-team/campaigns/:campaignId/workspace',
            element: deferred(<RedTeamWorkspacePage />),
          },
        ],
      },
    ]
  : [];

const orcaSuperAdminRoutes: RouteObject[] = INCLUDES_ORCA
  ? [
      { path: 'appbench-datasets', element: deferred(<SuperAdminAppBenchDatasetsPage />) },
      { path: 'orca-credits', element: deferred(<OrcaCreditsPage />) },
    ]
  : [];

export const router = createBrowserRouter([
  // Participant confirm/decline links: public, outside the app shell and login.
  ...(INCLUDES_COLLECT ? [{ path: '/rsvp/:token', element: deferred(<SchedulingRsvpPage />) }] : []),
  {
    path: '/',
    element: <AppShell />,
    children: [
      // Public routes
      { index: true, element: <RootRedirect /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'signup', element: <SignUpPage /> },
      { path: 'verify-email', element: <EmailVerificationPage /> },
      { path: 'auth/action', element: <AuthActionPage /> },
      { path: 'privacy', element: deferred(<PrivacyPolicyPage />) },
      { path: 'terms', element: deferred(<TermsOfUsePage />) },

      // Authenticated routes
      {
        element: <ProtectedRoute />,
        children: [
          // Account details are available to every authenticated role.
          { path: 'profile', element: deferred(<ProfilePage />) },
          {
            element: <NotForSchedulingStaff />,
            children: [...collectAccountRoutes, ...orcaAccountRoutes],
          },
          ...collectRoutes,
          ...orcaRoutes,

          // Admin-only routes
          {
            path: 'admin',
            element: <AdminRoute />,
            children: [
              ...collectAdminRoutes,
              { path: 'organization', element: deferred(<MyOrganizationPage />) },
              ...orcaAdminRoutes,
            ],
          },

          // Super-Admin routes
          {
            path: 'super-admin',
            element: <SuperAdminRoute />,
            children: [
              { path: 'tenants', element: deferred(<TenantsPage />) },
              ...orcaSuperAdminRoutes,
            ],
          },
        ],
      },

      // Catch-all
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
